// styles
import classnames from 'classnames/bind';
import Button from 'components/Button';
import RemoveButton from 'components/Button/RemoveButton';
// components
import RemoveModal from 'components/instances/RemoveModal';
import Table from 'components/Table';
import Typography from 'components/Typography';
// containers
import ManagementWrapper from 'containers/ManagementWrapper';
import { isEmpty } from 'lodash';
import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { ReactComponent as EyeIcon } from 'icons/eye.svg';
import { ReactComponent as EyeOffIcon } from 'icons/eye-off.svg';
import {
  ADD_STUDENTS_FULL,
  ClASSESS_FULL,
  getManageClassRoute,
  getManageStudentsRoute,
  getManageUserRoute,
  getResetAllRoute,
} from 'routes';
import { setCurrentClassroom, setOperatingClassroom } from 'store/actions/classroom';
// utils
import { clearStudentInfo, getStudent, getUser, removeStudent } from 'store/actions/user';
import { alphabeticalSort } from 'utils/sort';

import styles from './ManageClass.module.scss';

const cx = classnames.bind(styles);

const ManageClass = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { classId } = useParams();
  const [userToRemove, setUserToRemove] = useState({});
  const { currClass = {} } = useSelector(({ user }) => {
    const currClass = user.classrooms.find(({ id }) => +id === +classId);
    return { currClass };
  });
  const [isShowMasterPassword, setShowMasterPassword] = useState(false);
  const handleToggleMasterPassword = () => setShowMasterPassword((prev) => !prev);

  const crumbs = { 'all classes': ClASSESS_FULL, [currClass.name]: getManageClassRoute(classId) };

  const handleRemoveStudent = (studentId) => {
    dispatch(removeStudent(studentId, classId));
    setUserToRemove({});
  };
  const passwordRenderer = ({ isShowMasterPassword, currClass }) => (
    <Typography type="h5">{isShowMasterPassword ? currClass.master_password : '••••••••••'}</Typography>
  );

  const usernameRenderer = ({ username }) => <Typography type="h5">{username}</Typography>;

  const deleteRenderer = useMemo(
    () => (userProps) =>
      (
        <div className={cx('remove-wrapper')}>
          <RemoveButton onClick={() => setUserToRemove(userProps)} />
        </div>
      ),
    [],
  );

  const studentRenderer = useMemo(
    () =>
      ({ id: userId, first_name = '', last_name_initial = '', username }) =>
        (
          <Typography className={cx('student-name')} onClick={() => navigate(getManageUserRoute(classId, userId))}>
            {first_name ? (
              <>
                {first_name} {!!last_name_initial && `${last_name_initial}.`}
              </>
            ) : (
              username
            )}
          </Typography>
        ),
    [classId, navigate],
  );

  const tableColumns = useMemo(
    () => [
      {
        name: 'Student',
        width: 325,
        sortFunction: alphabeticalSort(['first_name', 'username']),
        renderer: studentRenderer,
      },
      { name: 'Username', width: 300, renderer: usernameRenderer },
      { name: 'Password', width: 250, renderer: (props) => passwordRenderer({ ...props, isShowMasterPassword, currClass }) },
      { name: '', width: 100, renderer: deleteRenderer },
    ],
    [deleteRenderer, studentRenderer, isShowMasterPassword],
  );

  const handleAddStudents = () => {
    dispatch(setOperatingClassroom(currClass));
    navigate(getManageStudentsRoute(classId));
  };

  return (
    <ManagementWrapper breadcrumbs={crumbs}>
      <div className={cx('heading-wrapper', 'mgb-35')}>
        <Typography type="h4">{currClass.name}</Typography>

        <div className={styles.classroomActions}>
          <Typography
            className={cx('reset-passwords')}
            type="body-xs"
            onClick={() => navigate(getResetAllRoute(classId))}
          >
            RESET ALL PASSWORDS
          </Typography>
          <Typography className={cx('see-passwords')} type="body-xs" onClick={handleToggleMasterPassword}>
            SEE MASTER PASSWORD
            {isShowMasterPassword ? (
              <EyeIcon className={cx('icon')} />
            ) : (
              <EyeOffIcon className={cx('icon')} />
            )}
          </Typography>

          <Button label="+ ADD STUDENTS" onClick={handleAddStudents} />
        </div>
      </div>

      <Table columns={tableColumns} data={currClass.students} />

      <RemoveModal
        subject={userToRemove.first_name || userToRemove.username}
        onConfirm={() => handleRemoveStudent(userToRemove.id)}
        isOpen={!isEmpty(userToRemove)}
        onClose={() => setUserToRemove({})}
      />
    </ManagementWrapper>
  );
};

export default ManageClass;
